import { useContext } from 'react'

import type { UseFormOptions } from '@domain-group/react-hooks'
import {
  useForm, useLatestEntity, useThunkDispatch,
} from '@domain-group/react-hooks'

import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import defaultState, { requiredFields } from '@models/agencyOrderBundle'
import type { AgencyOrderBundleModel } from '@models/types'

import * as agencyOrderBundleActions from '@redux/modules/agencyOrderBundle'
import type { AppDispatch } from '@redux/store'
import type { EntityOptions } from '@redux/modules/module'
import { launchModal } from '@domain-group/javascript-utils'

export type BulkCreateParams = {
  agency_id?: number,
  agency_ids?: number[],
  order_bundle_id?: number,
  order_bundle_ids?: number[],
}

export type BulkCreateAgencyOrderBundlesParams = {
  bulkCreateParams: BulkCreateParams,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

const bulkCreateAgencyOrderBundles = (params: BulkCreateAgencyOrderBundlesParams) => {
  const { bulkCreateParams, dispatch, requestOptions } = params

  const { bulkCreateAgencyOrderBundles: bulkCreateFn } = agencyOrderBundleActions

  return dispatch(bulkCreateFn(bulkCreateParams, requestOptions))
}

export type CreateAgencyOrderBundleParams = {
  agencyOrderBundleParams: AgencyOrderBundleModel,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

const createAgencyOrderBundle = (params: CreateAgencyOrderBundleParams) => {
  const { agencyOrderBundleParams, dispatch, requestOptions } = params
  const { createAgencyOrderBundle: createFn } = agencyOrderBundleActions

  return dispatch(createFn(agencyOrderBundleParams, requestOptions))
}

export type DeleteAgencyOrderBundleParams = {
  agencyOrderBundle: AgencyOrderBundleModel,
  dispatch: AppDispatch,
}

const deleteAgencyOrderBundle = (params: DeleteAgencyOrderBundleParams) => {
  const { agencyOrderBundle, dispatch } = params
  const { deleteAgencyOrderBundle: deleteFn } = agencyOrderBundleActions

  return dispatch(deleteFn(agencyOrderBundle))
}

export type BulkDeleteAgencyOrderBundlesParams = {
  agencyOrderBundleParams: {
    agency_order_bundle_ids?: number[],
    order_bundle_id: number,
  },
  dispatch: AppDispatch,
}

const bulkDeleteAgencyOrderBundles = (params: BulkDeleteAgencyOrderBundlesParams) => {
  const { agencyOrderBundleParams, dispatch } = params
  const { bulkDeleteAgencyOrderBundles: deleteFn } = agencyOrderBundleActions

  return dispatch(deleteFn(agencyOrderBundleParams))
}

export type ToggleVisibilityParams = {
  agencyOrderBundle: AgencyOrderBundleModel,
  dispatch: AppDispatch,
}

const toggleVisibility = (params: ToggleVisibilityParams) => {
  const { agencyOrderBundle, dispatch } = params
  const { toggleVisibility: toggleVisibilityFn } = agencyOrderBundleActions

  return dispatch(toggleVisibilityFn(agencyOrderBundle))
}

export function useAgencyOrderBundleForm(
  agencyOrderBundle: Partial<AgencyOrderBundleModel> = {},
  options: UseFormOptions = {},
) {
  const { validateOn } = options

  const agencyOrderBundleForm = useForm(
    defaultState,
    {
      entity: agencyOrderBundle,
      requiredFields,
      validateOn,
    },
    [agencyOrderBundle.id, agencyOrderBundle.cache_key],
  )

  return {
    ...agencyOrderBundleForm,
  }
}

export function useRelations(agencyOrderBundle: AgencyOrderBundleModel) {
  const { agency_id, order_bundle_id } = agencyOrderBundle || {}

  const { agencies, orderBundles, orderBundleItems: items } = useSelector(reduxState => reduxState.entities)

  const agency = agencies[agency_id] || {}
  const orderBundle = orderBundles[order_bundle_id]
  const orderBundleItems = Object.values(items).filter(bundleItems => bundleItems.order_bundle_id === order_bundle_id)

  return {
    agency,
    orderBundle,
    orderBundleItems,
  }
}

function useAgencyOrderBundle(initEntity: Partial<AgencyOrderBundleModel> = {}) {
  // @ts-expect-error TS2322
  const {
    entity: agencyOrderBundle,
  // @ts-expect-error TS2345
  }: { entity: AgencyOrderBundleModel } = useLatestEntity(initEntity, 'agencyOrderBundles')

  const dispatch = useThunkDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    creating, deleting, loading, updating,
  } = useSelector(reduxState => reduxState.agencyOrderBundles)

  const { agency, orderBundle } = useRelations(agencyOrderBundle)

  return {
    agency,
    agencyOrderBundle,
    callbacks: {
      bulkCreate: () => launchModal({
        callbacks,
        modalKey: 'BulkCreateAgencyOrderBundlesModal',
        payload: { agencyOrderBundle },
      }),
      bulkCreateAgencyOrderBundles: (
        bulkCreateParams: BulkCreateAgencyOrderBundlesParams,
        requestOptions: EntityOptions,
      ) => (
        // @ts-expect-error TS2559
        bulkCreateAgencyOrderBundles({ bulkCreateParams, dispatch, requestOptions })
      ),
      bulkDeleteAgencyOrderBundles: (agencyOrderBundleParams: AgencyOrderBundleModel) => (
        bulkDeleteAgencyOrderBundles({ agencyOrderBundleParams, dispatch })
      ),
      createAgencyOrderBundle: (agencyOrderBundleParams: AgencyOrderBundleModel, requestOptions: EntityOptions) => (
        createAgencyOrderBundle({ agencyOrderBundleParams, dispatch, requestOptions })
      ),
      deleteAgencyOrderBundle: () => deleteAgencyOrderBundle({ agencyOrderBundle, dispatch }),
      manageAgencyOrderBundle: () => launchModal({
        callbacks,
        modalKey: 'ManageAgencyOrderBundleModal',
        payload: { agencyOrderBundle },
      }),
      toggleVisibility: () => toggleVisibility({ agencyOrderBundle, dispatch }),
    },
    creating,
    deleting,
    loading,
    orderBundle,
    updating,
  }
}

export default useAgencyOrderBundle
